import { useEffect, useState } from 'react'

import { APP_VERSION, fetchRemoteVersion } from './version.utils'

const CHECK_INTERVAL_MS = 5 * 60 * 1000

/**
 * Polls /version.json (every 5 min, when the tab regains focus and when the network comes back)
 * and returns true once the server serves a build different from the one this tab is running.
 * Once true it stays true: the only way back is a reload.
 */
export default function useVersionCheck() {
    const [newVersionAvailable, setNewVersionAvailable] = useState(false)

    useEffect(() => {
        if (import.meta.env.DEV || newVersionAvailable) return undefined

        let cancelled = false

        const check = async () => {
            try {
                const remoteVersion = await fetchRemoteVersion()
                if (cancelled || !remoteVersion) return
                if (remoteVersion !== APP_VERSION) setNewVersionAvailable(true)
            } catch {
                // offline or transient error: the next tick will retry
            }
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') check()
        }

        const intervalId = setInterval(check, CHECK_INTERVAL_MS)
        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('online', check)

        return () => {
            cancelled = true
            clearInterval(intervalId)
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('online', check)
        }
    }, [newVersionAvailable])

    return newVersionAvailable
}
